import { datadogRum } from "@datadog/browser-rum";
import { useInterval } from "@mantine/hooks";
import { AppRoute } from "app";
import { useAtomValue, useSetAtom } from "jotai";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";

import { getEstimate } from "api/getEstimate";
import { ESTIMATE_POLL_DURATION, ESTIMATE_POLL_DURATION_DEV } from "app/constants";
import { clientConfigAtom } from "atoms/clientConfig";
import { estimateAtom } from "atoms/estimate";
import { sessionIdAtom } from "atoms/sessionId";
import { usePageRouting } from "hooks/usePageRouting";
import { Estimate } from "types/Estimate";

import { clientIdAtom } from "../../../atoms/clientId";
import { EndStateRoutes, EstimateEndStateRoutes } from "../types";
import { EnvironmentStage, getRuntimeEnvironmentStage } from '../../../utils/environment/getRuntimeEnvironmentStage';

type EndStateKeys = keyof Omit<EndStateRoutes, "onTimeout" | "onApiError">;
const endStates: Record<EndStateKeys, (estimate: Estimate) => boolean> = {
  repairable: ({ isEstimateDisplayEnabled, isInScope, isTotalLoss }) =>
    isEstimateDisplayEnabled && isInScope && !isTotalLoss,
  totalLoss: ({ isInScope, isTotalLoss }) => isInScope && isTotalLoss,
  outOfScope: ({ isInScope }) => !isInScope,
};

export const usePollEstimate = () => {
  const runtimeEnvironmentStage: EnvironmentStage | null = getRuntimeEnvironmentStage();
  const sessionId = useAtomValue(sessionIdAtom);
  const clientId = useAtomValue(clientIdAtom);
  const setEstimate = useSetAtom(estimateAtom);
  const { estimate: estimateConfig } = useAtomValue(clientConfigAtom);
  const [countdown, setCountdown] = useState(
    (
      runtimeEnvironmentStage === EnvironmentStage.Development || 
      runtimeEnvironmentStage === EnvironmentStage.Local
    ) ? ESTIMATE_POLL_DURATION_DEV : ESTIMATE_POLL_DURATION);
  const polling = useInterval(() => setCountdown((timer) => timer - 1), 1000);

  const { navigateTo } = usePageRouting();

  const handleFlowCompletedRedirect = (route: EstimateEndStateRoutes) => {
    navigateTo(configRouteMap[route]);
  };

  const configRouteMap = {
    [EstimateEndStateRoutes.OutOfScopeRoute]: AppRoute.EstimateOutOfScope,
    [EstimateEndStateRoutes.RepairableRoute]: AppRoute.EstimateRepariable,
    [EstimateEndStateRoutes.TotalLossRoute]: AppRoute.EstimateTotalLoss,
    [EstimateEndStateRoutes.FlowCompleteRoute]: AppRoute.FlowCompleted,
    [EstimateEndStateRoutes.ExternalRedirect]: AppRoute.ExternalRedirect,
    [EstimateEndStateRoutes.ErrorPageRoute]: AppRoute.ApiError,
  };

  useQuery("estimate", () => getEstimate(sessionId, clientId), {
    refetchInterval: 5000,
    refetchOnWindowFocus: true,
    onSuccess: ({ estimate }) => {
      if (estimate !== null) {
        datadogRum.addAction("Received Estimate", { timestamp: Date.now() });

        for (const [endState, goToRoute] of Object.entries(endStates)) {
          // Object.entries always assumes key is a string, which it is but we've narrowed it down to our enum
          const endStateRoutesConfig = estimateConfig?.endStateRoutes[endState as EndStateKeys];
          if (goToRoute(estimate) && endStateRoutesConfig) {
            setEstimate(estimate);
            navigateTo(configRouteMap[endStateRoutesConfig]);
            return;
          }
        }

        // Landing here means config didn't match up with Estimate results
        handleFlowCompletedRedirect(EstimateEndStateRoutes.FlowCompleteRoute);
      }

      if (countdown <= 0) {
        const onTimeoutRouteConfig =
          estimateConfig?.endStateRoutes["onTimeout"] || EstimateEndStateRoutes.FlowCompleteRoute;

        datadogRum.addAction("Timeout polling for Estimate", { timestamp: Date.now() });

        handleFlowCompletedRedirect(onTimeoutRouteConfig);
      }
    },
    onError: (error) => {
      datadogRum.addError(error, {
        message: "Failed to get estimate",
      });

      const onErrorRouteConfig =
        estimateConfig?.endStateRoutes["onApiError"] || EstimateEndStateRoutes.FlowCompleteRoute;

      handleFlowCompletedRedirect(onErrorRouteConfig);
    },
  });

  useEffect(() => {
    polling.start();

    datadogRum.addAction("Start polling for Estimate", { timestamp: Date.now() });

    return () => {
      polling.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
